import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import {
    Row,
    Col,
    Card,
    CardBody,
    CardHeader,
    Button,
    FormGroup,
    Label,
    Input,
    Alert,
    Table,
    Badge,
    Spinner,
    Container,
    Modal,
    ModalHeader,
    ModalBody
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { FaMoneyBillWave, FaFileInvoice, FaTruck, FaPlus, FaMinus, FaList, FaUser, FaCalendarAlt, FaEdit, FaTrash, FaShieldAlt, FaArrowUp, FaArrowDown, FaBalanceScale, FaArrowLeft } from 'react-icons/fa';
import { reglementActions } from '../../sagas/reglementSlice';
import { formatPrice, preventScroll } from '../../helpers/utility';
import Select from 'react-select';
import DocumentSelectionModal from './DocumentSelectionModal';
import './ReglementForm.css'; // Assuming you have some custom styles
import { clientActions } from '../../sagas/clientSlice';
import { deliveryActions } from '../../sagas/deliveryNoteSlice';
import { invoiceActions } from '../../sagas/invoiceSlice';
import { useLocation, useNavigate } from 'react-router-dom';

const ReglementForm = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { loading } = useSelector(state => state.reglements);
    const { invoices: facturesImpayees } = useSelector((state) => state.invoices);
    const { list: bonLivraisonsNonRegles } = useSelector((state) => state.deliveryNotes);

    const { clients } = useSelector(state => state.clients);

    const [selectedClient, setSelectedClient] = useState(null);
    const [selectedChantier, setSelectedChantier] = useState(null);
    const [typeReglement, setTypeReglement] = useState('AVANCE');
    const [selectedDocuments, setSelectedDocuments] = useState([]);
    const [showDocumentModal, setShowDocumentModal] = useState(false);
    const [showAvanceModal, setShowAvanceModal] = useState(false);
    const [avanceDocFilter, setAvanceDocFilter] = useState("NONE");
    const [utilisationAvances, setUtilisationAvances] = useState([]);
    const typeReglementOptions = [
        // { value: 'FACTURE', label: t('reglement.factures'), icon: FaFileInvoice },
        // { value: 'BON_LIVRAISON', label: t('reglement.bonLivraisons'), icon: FaTruck },
        { value: 'AVANCE', label: t('reglement.avance'), icon: FaMoneyBillWave },
        { value: 'CAUTION', label: t('reglement.caution'), icon: FaMoneyBillWave }
    ];
    const avanceDocFilterOptions = [
        { value: "NONE", label: t('reglement.none') },
        { value: "FACTURE", label: t('reglement.factures') },
        { value: "BL", label: t('reglement.bonLivraisons') },
        { value: "BOTH", label: t('reglement.both') }
    ];
    const { loading: loadingReglements, error: errorReglements, echeances, cautions } = useSelector((state) => state.reglements);
    const [showEcheanceModal, setShowEcheanceModal] = useState(false);
    const [showCautionModal, setShowCautionModal] = useState(false);
    const [filters, setFilters] = useState({
        clientId: null,
        dateFrom: null,
        dateTo: null,
        statut: "EN_ATTENTE"
    });
    const navigate = useNavigate();
    const location = useLocation();
    const reglement = location.state?.reglement || null;
    const canEdit = reglement && reglement.statut === 'EN_ATTENTE' || !reglement;
    // Validation schema
    const validationSchema = Yup.object().shape({
        clientId: Yup.number().required(t('reglement.clientRequired')),
        // montantRegle: Yup.number()
        //     .positive(t('reglement.montantPositive'))
        //     .required(t('reglement.montantRequired')),
        dateReglement: Yup.date().required(t('reglement.dateReglementRequired')),
        modeReglement: Yup.string().required(t('reglement.modeReglementRequired')),
        statut: Yup.string().required(t('reglement.statutRequired')),

    });

    // Initial values
    const initialValues = {
        id: reglement?.id || null,
        reference: reglement?.reference || '',
        clientId: reglement?.clientId || '',
        numFactures: reglement?.numFactures || '',
        numBonLivraisons: reglement?.numBonLivraisons || '',
        typeReglement: reglement?.typeReglement || 'AVANCE',
        montantRegle: reglement?.montantRegle || 0,
        montantAffecte: reglement?.montantAffecte || 0,
        dateReglement: reglement?.dateReglement || new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Casablanca' }),
        dateEncaissement: reglement?.dateEncaissement || '',
        statut: reglement?.statut || 'EN_ATTENTE',
        statutPrecedent: reglement?.statutPrecedent || null,
        commentaire: reglement?.commentaire || '',
        modeReglement: reglement?.modeReglement || 'ESPECES',
        referencePaiement: reglement?.referencePaiement || '',
        numeroCheque: reglement?.numeroCheque || '',
        banque: reglement?.banque || '',
        dateEcheance: reglement?.dateEcheance || '',
        motifAvance: reglement?.motifAvance || '',
        dateValiditeAvance: reglement?.dateValiditeAvance || '',
        commentaireAvance: reglement?.commentaireAvance || '',
    };

    const modeReglementOptions = [
        { value: 'ESPECES', label: t('reglement.especes') },
        { value: 'CHEQUE', label: t('reglement.cheque') },
        { value: 'VIREMENT', label: t('reglement.virement') },
        { value: 'CARTE_BANCAIRE', label: t('reglement.carteBancaire') },
    ];

    const clientOptions = clients?.map(client => ({
        solde: client.solde,
        totalDebit: client.totalDebit,
        totalCredit: client.totalCredit,
        value: client.id,
        label: client.typeClient === 'PARTICULIER'
            ? `${client.nom} ${client.prenom || ''} `
            : client.entreprise,
        chantiers: client.chantiers
    })) ?? [];

    // Create chantier options from selected client's chantiers
    const chantierOptions = selectedClient?.chantiers?.map(chantier => ({
        value: chantier.id,
        label: `${chantier.code} - ${chantier.nom}`,
        ...chantier
    })) || [];

    const getTotalMontant = (items) => {
        return items.reduce((total, item) => total + item.montantRegle, 0);
    };

    useEffect(() => {
        dispatch(clientActions.search({
            query: ""
        }));

    }, []);

    console.log(facturesImpayees)
    // Initialize form with existing reglement data
    useEffect(() => {
        if (reglement) {
            // Set selected client
            const client = clients.find(c => c.id === reglement.clientId);
            if (client) {
                setSelectedClient({
                    value: client.id,
                    label: client.typeClient === 'PARTICULIER'
                        ? `${client.prenom} ${client.nom}`
                        : client.entreprise,
                    solde: client.solde,
                    totalDebit: client.totalDebit,
                    totalCredit: client.totalCredit,
                    chantiers: client.chantiers
                });

                // Set selected chantier if exists
                if (reglement.chantierId && client.chantiers) {
                    const chantier = client.chantiers.find(ch => ch.id === reglement.chantierId);
                    if (chantier) {
                        setSelectedChantier({
                            value: chantier.id,
                            label: `${chantier.code} - ${chantier.nom}`,
                            ...chantier
                        });
                    }
                }

                dispatch(reglementActions.echeanceClient({ ...filters, clientId: client.id }));
                dispatch(reglementActions.cautionClient({ ...filters, clientId: client.id }));
            }

            setTypeReglement(reglement.typeReglement);

        }
    }, [reglement, clients]);
   

    // Handle document selection from modal
    const handleDocumentsSelected = (documents) => {
        setSelectedDocuments(documents);
    };

    // Calculate total amount
    const calculateTotalAmount = () => {
        console.log(selectedDocuments);
        return selectedDocuments.reduce((total, doc) => {
            if (typeReglement === 'FACTURE') {
                return total + (doc.montantTotalTTC - doc.montantRegle || 0);
            } else if (typeReglement === 'BON_LIVRAISON') {
                return total + (doc.montantTotalTTC - doc.montantPaye || 0);
            }
        }, 0);
    };

    // Remove document from selection
    const removeDocument = (documentId) => {
        setSelectedDocuments(prev => prev.filter(doc => doc.id !== documentId));
    };


    const handleSubmit = (values, { setSubmitting }) => {
        const payload = {
            data: {
                ...values,
                typeReglement: typeReglement,
                chantierId: selectedChantier ? selectedChantier.value : null,
                numFactures: selectedDocuments
                    .filter(doc => doc.numero?.toString().startsWith("FC"))
                    .map(doc => doc.numero)
                    .join(','),
                numBonLivraisons: selectedDocuments
                    .filter(doc => doc.numero?.toString().startsWith("BL") || doc.numero?.toString().startsWith("CBL"))
                    .map(doc => doc.numero)
                    .join(','),
                montantRegle: values.montantRegle || calculateTotalAmount(),
                montantAffecte: typeReglement === 'AVANCE' ? 0 : calculateTotalAmount(),
                documentsIds: typeReglement === 'AVANCE' ? [] : selectedDocuments.map(doc => doc.id),
                estAvance: typeReglement === 'AVANCE',

                utilisationAvances: utilisationAvances, // Nouvelles utilisations d'avances
                // Champs spécifiques aux avances
                dateValiditeAvance: typeReglement === 'AVANCE' ? values.dateValiditeAvance : null,
                commentaireAvance: typeReglement === 'AVANCE' ? values.commentaireAvance : null,
            },
            onSuccess: (data) => {
                Swal.fire({
                    icon: 'success',
                    title: t('common.success'),
                    text: reglement ? t('reglement.updateSuccess') :
                        typeReglement === 'AVANCE' ? t('reglement.avanceCreateSuccess') :
                            t('reglement.createSuccess'),
                });
                dispatch(reglementActions.searchRequest(filters));
                navigate("/paiements");
            },
            onError: (error) => {
                Swal.fire({
                    icon: 'error',
                    title: t('common.error'),
                    text: error.message || t('common.errorOccurred'),
                });
                setSubmitting(false);
            }
        };

        if (reglement) {
            dispatch(reglementActions.updateRequest({ ...payload, id: reglement.id }));
        } else {
            dispatch(reglementActions.createRequest(payload));
        }
    };




    return (
        <Container fluid>
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                enableReinitialize
            >
                {({ values, setFieldValue, isSubmitting, errors, touched, handleChange, handleBlur }) => (
                    <Form>
                        <Row>
                            {/* Section Client et Type */}
                            <Col md={12}>
                                <Card className="mb-3 shadow-sm">
                                    <CardHeader className="bg-primary text-white">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <h5 className="mb-0">
                                                <FaMoneyBillWave className="me-2" />
                                                {t('reglement.informationsGenerales')}
                                            </h5>
                                            <Button
                                                color="light"
                                                size="sm"
                                                onClick={() => navigate(-1)}
                                                className="d-flex align-items-center"
                                            >
                                                <FaArrowLeft className="me-2" />
                                                {t('common.back', 'Retour')}
                                            </Button>
                                        </div>
                                    </CardHeader>
                                    <CardBody>
                                        <Row className="g-3 align-items-center">
                                            <Col md={3}>
                                                <FormGroup>
                                                    <Label for="clientId" className="fw-bold text-secondary">
                                                        <FaUser className="me-1" />
                                                        {t('reglement.client')} *
                                                    </Label>
                                                    <Select
                                                        options={clientOptions}
                                                        value={selectedClient}
                                                        onChange={(option) => {
                                                            if (canEdit) {
                                                                setSelectedClient(option);
                                                                setSelectedChantier(null); // Reset chantier selection
                                                                setFieldValue('clientId', option?.value || '');
                                                                setSelectedDocuments([]);
                                                                if (option) {
                                                                    dispatch(reglementActions.echeanceClient({ ...filters, clientId: option.value }));
                                                                    dispatch(reglementActions.cautionClient({ ...filters, clientId: option.value }));
                                                                    setFilters(f => ({ ...f, clientId: option.value }));
                                                                }
                                                            }
                                                        }}
                                                        placeholder={t('reglement.selectClient')}
                                                        isClearable={!reglement}
                                                        isSearchable={!reglement}
                                                        isDisabled={!canEdit}
                                                        className={`rounded-3 border border-2 ${errors.clientId && touched.clientId ? 'is-invalid' : ''}`}
                                                        menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                                                        styles={{
                                                            menuPortal: base => ({ ...base, zIndex: 9999 })
                                                        }}
                                                    />
                                                    <ErrorMessage name="clientId" component="div" className="text-danger small mt-1" />
                                                </FormGroup>
                                            </Col>
                                            {/* {typeReglement === "AVANCE" && (
                                                <Col md={2}>
                                                    <FormGroup>
                                                        <Label className="fw-bold text-secondary">
                                                            {t('reglement.documentsARegler')}
                                                        </Label>

                                                        <Select
                                                            options={avanceDocFilterOptions}
                                                            value={avanceDocFilterOptions.find(o => o.value === avanceDocFilter)}
                                                            onChange={(opt) => {
                                                                setAvanceDocFilter(opt.value);
                                                                setSelectedDocuments([]);
                                                                if (selectedClient) {
                                                                    // Trigger reload
                                                                    if (opt.value !== avanceDocFilter) {
                                                                        // force useEffect reload
                                                                    }
                                                                }
                                                            }}
                                                            className="rounded-3 border border-2"
                                                        />
                                                    </FormGroup>
                                                </Col>
                                            )} */}
                                            <Col md={2}>
                                                <FormGroup>
                                                    <Label for="dateReglement" className="fw-bold text-secondary">
                                                        <FaCalendarAlt className="me-1" />
                                                        {t('reglement.dateReglement')} *
                                                    </Label>
                                                    <Field
                                                        name="dateReglement"
                                                        type="date"
                                                        className={`form-control rounded-3 border border-2 ${errors.dateReglement && touched.dateReglement ? 'is-invalid' : ''}`}
                                                        disabled={!canEdit}
                                                    />
                                                    <ErrorMessage name="dateReglement" component="div" className="invalid-feedback" />
                                                </FormGroup>
                                            </Col>
                                            <Col md={2} >
                                                <FormGroup>
                                                    <Label for="dateEncaissement" className="fw-bold text-secondary">
                                                        <FaCalendarAlt className="me-1" />
                                                        {t('reglement.dateEncaissement')}
                                                    </Label>
                                                    <Field
                                                        name="dateEncaissement"
                                                        type="date"
                                                        className="form-control rounded-3 border border-2"
                                                        disabled={!canEdit}
                                                    />
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                        {selectedClient && (
                                            <Row className="mt-2 g-2">
                                                <Col>
                                                    <Row className="g-2 align-items-stretch">
                                                        <Col xs={6} md={2}>
                                                            <Card className="shadow-sm h-100 bg-light border-0 rounded-2">
                                                                <CardBody className="d-flex flex-column justify-content-center align-items-center py-2">
                                                                    <FaBalanceScale className="mb-1 text-success" size={20} />
                                                                    <span className="text-muted text-uppercase" style={{ fontSize: '0.7rem' }}>{t('client.balance')}</span>
                                                                    {selectedClient.totalCredit > selectedClient.totalDebit ? (
                                                                        <span className="fw-bold text-success" style={{ fontSize: '0.9rem' }}>+{formatPrice(Math.abs(selectedClient.solde))}</span>
                                                                    ) : selectedClient.totalCredit < selectedClient.totalDebit ? (
                                                                        <span className="fw-bold text-danger" style={{ fontSize: '0.9rem' }}>-{formatPrice(Math.abs(selectedClient.solde))}</span>
                                                                    ) : (
                                                                        <span className="fw-bold text-muted" style={{ fontSize: '0.9rem' }}>{formatPrice(0)}</span>
                                                                    )}
                                                                </CardBody>
                                                            </Card>
                                                        </Col>
                                                        <Col xs={6} md={2}>
                                                            <Card className="shadow-sm h-100 bg-light border-0 rounded-2">
                                                                <CardBody className="d-flex flex-column justify-content-center align-items-center py-2">
                                                                    <FaArrowDown className="mb-1 text-danger" size={20} />
                                                                    <span className="text-muted text-uppercase" style={{ fontSize: '0.7rem' }}>{t('client.totalDebit')}</span>
                                                                    <span className="fw-bold" style={{ fontSize: '0.9rem' }}>{formatPrice(selectedClient.totalDebit)}</span>
                                                                </CardBody>
                                                            </Card>
                                                        </Col>
                                                        <Col xs={6} md={2}>
                                                            <Card className="shadow-sm h-100 bg-light border-0 rounded-2">
                                                                <CardBody className="d-flex flex-column justify-content-center align-items-center py-2">
                                                                    <FaArrowUp className="mb-1 text-primary" size={20} />
                                                                    <span className="text-muted text-uppercase" style={{ fontSize: '0.7rem' }}>{t('client.totalCredit')}</span>
                                                                    <span className="fw-bold" style={{ fontSize: '0.9rem' }}>{formatPrice(selectedClient.totalCredit)}</span>
                                                                </CardBody>
                                                            </Card>
                                                        </Col>
                                                        <Col xs={6} md={3}>
                                                            <Card className="shadow-sm h-100 bg-warning bg-opacity-10 border-0 rounded-2">
                                                                <CardBody className="d-flex flex-column justify-content-center align-items-center py-2">
                                                                    <div className="d-flex align-items-center mb-1">
                                                                        <FaCalendarAlt className="me-1 text-warning" size={16} />
                                                                        <span className="text-muted text-uppercase" style={{ fontSize: '0.7rem' }}>{t('client.echeances')}</span>
                                                                    </div>
                                                                    <span className="fw-bold text-warning" style={{ fontSize: '0.9rem' }}>{echeances?.length || 0}</span>
                                                                    <Button color="link" size="sm" className="p-0 fw-bold text-warning" style={{ fontSize: '0.7rem' }} onClick={() => setShowEcheanceModal(true)}>
                                                                        {t('client.viewDetails')}
                                                                    </Button>
                                                                </CardBody>
                                                            </Card>
                                                        </Col>
                                                        <Col xs={6} md={3}>
                                                            <Card className="shadow-sm h-100 bg-info bg-opacity-10 border-0 rounded-2">
                                                                <CardBody className="d-flex flex-column justify-content-center align-items-center py-2">
                                                                    <div className="d-flex align-items-center mb-1">
                                                                        <FaShieldAlt className="me-1 text-info" size={16} />
                                                                        <span className="text-muted text-uppercase" style={{ fontSize: '0.7rem' }}>{t('client.cautions')}</span>
                                                                    </div>
                                                                    <span className="fw-bold text-info" style={{ fontSize: '0.9rem' }}>{cautions?.length || 0}</span>
                                                                    <Button color="link" size="sm" className="p-0 fw-bold text-info" style={{ fontSize: '0.7rem' }} onClick={() => setShowCautionModal(true)}>
                                                                        {t('client.viewDetails')}
                                                                    </Button>
                                                                </CardBody>
                                                            </Card>
                                                        </Col>
                                                    </Row>
                                                </Col>
                                            </Row>
                                        )}
                                    </CardBody>
                                </Card>
                            </Col>

                            {/* Section Documents à régler */}
                            {selectedClient && (["FACTURE", "BON_LIVRAISON"].includes(typeReglement) || (typeReglement === 'AVANCE' && avanceDocFilter != 'NONE')) && (
                                <Col md={12}>
                                    <Card className="mb-3 shadow-sm">
                                        <CardHeader className="d-flex justify-content-between align-items-center bg-light">
                                            <h5 className="mb-0">
                                                {typeReglement === 'FACTURE' && <FaFileInvoice className="me-2 text-primary" />}
                                                {typeReglement === 'BON_LIVRAISON' && <FaTruck className="me-2 text-success" />}
                                                {typeReglement === 'AVANCE' && <FaMoneyBillWave className="me-2 text-warning" />}

                                                {typeReglement === 'FACTURE' && t('reglement.facturesImpayees')}
                                                {typeReglement === 'BON_LIVRAISON' && t('reglement.bonLivraisonsNonRegles')}
                                                {typeReglement === 'AVANCE' && t('reglement.selectDocumentsAvance')}
                                            </h5>
                                            <Button
                                                color="primary"
                                                size="sm"
                                                onClick={() => setShowDocumentModal(true)}
                                                disabled={!selectedClient || !!reglement}
                                            >
                                                <FaList className="me-1" />
                                                {t('reglement.selectDocuments')}
                                            </Button>
                                        </CardHeader>
                                        <CardBody>
                                            {/* Show references in edit mode */}
                                            {reglement && (
                                                <Alert color="secondary" className="mb-3">
                                                    <strong>{typeReglement === 'FACTURE' ? t('reglement.numFactures', 'Références factures') : t('reglement.numBonLivraisons', 'Références BL')}:</strong>
                                                    <br />
                                                    <span className="text-dark">
                                                        {typeReglement === 'FACTURE'
                                                            ? (reglement.numFactures || t('reglement.noFactureReference', 'Aucune référence'))
                                                            : (reglement.numBonLivraisons || t('reglement.noBLReference', 'Aucune référence'))}
                                                    </span>
                                                </Alert>
                                            )}
                                            {selectedDocuments.length === 0 ? (
                                                <Alert color="info" className="text-center mb-0">
                                                    <FaPlus className="me-2" />
                                                    {t('reglement.noDocumentsSelected')}
                                                    <br />
                                                    <small className="text-muted">{t('reglement.clickSelectDocuments')}</small>
                                                </Alert>
                                            ) : (
                                                <>
                                                    <div className="table-responsive">
                                                        <Table striped bordered hover size="sm">
                                                            <thead className="table-light">
                                                                <tr>
                                                                    <th>{t('reglement.numero')}</th>
                                                                    <th>{t('reglement.date')}</th>
                                                                    <th>{t('reglement.montantTotal')}</th>
                                                                    <th>{t('reglement.montantPaye')}</th>
                                                                    <th>{t('reglement.montantRestant')}</th>
                                                                    <th>{t('reglement.statut')}</th>
                                                                    <th width="80">{t('common.actions')}</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {selectedDocuments.map((document) => (
                                                                    <tr key={document.id}>
                                                                        <td><strong>{document.numero}</strong>
                                                                        </td>
                                                                        <td>
                                                                            {document.createdAt || document.dateFacture}
                                                                        </td>
                                                                        <td>
                                                                            {formatPrice(document.montantTotalTTC)}
                                                                        </td>
                                                                        <td>
                                                                            {formatPrice(document.montantPaye || document.montantRegle || 0)}
                                                                        </td>
                                                                        <td className="fw-bold text-danger">
                                                                            {formatPrice(
                                                                                document.montantRestant ||
                                                                                (document.montantTotalTTC - (document.montantPaye || document.montantRegle || 0))
                                                                            )}
                                                                        </td>
                                                                        <td>
                                                                            <Badge color={
                                                                                (document.montantPaye || document.montantRegle || 0) > 0 ? 'warning' : 'danger'
                                                                            }>
                                                                                {(document.montantPaye || document.montantRegle || 0) > 0
                                                                                    ? t('reglement.partiel')
                                                                                    : t('reglement.impaye')
                                                                                }
                                                                            </Badge>
                                                                        </td>
                                                                        <td>
                                                                            <Button
                                                                                color="danger"
                                                                                size="sm"
                                                                                onClick={() => removeDocument(document.id)}
                                                                                title={t('common.remove')}
                                                                            >
                                                                                <FaMinus />
                                                                            </Button>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                            <tfoot className="table-light">
                                                                <tr>
                                                                    <th colSpan="4" className="text-end">
                                                                        {t('reglement.totalSelectionne')}:
                                                                    </th>
                                                                    <th className="text-danger">
                                                                        {formatPrice(calculateTotalAmount())}
                                                                    </th>
                                                                    <th colSpan="2"></th>
                                                                </tr>
                                                            </tfoot>
                                                        </Table>
                                                    </div>

                                                    <Alert color="success" className="mt-3">
                                                        <Row>
                                                            <Col md={6}>
                                                                <strong>{t('reglement.documentsSelectionnes')}:</strong> {selectedDocuments.length}
                                                            </Col>
                                                            <Col md={6} className="text-end">
                                                                <strong>{t('reglement.montantTotal')}:</strong> {formatPrice(calculateTotalAmount())}
                                                            </Col>
                                                        </Row>
                                                    </Alert>
                                                </>
                                            )}

                                        </CardBody>
                                    </Card>
                                </Col>
                            )}

                        </Row>
                        <Card>

                            <CardBody>
                                <Row>
                                    <Col md={3}>
                                        <FormGroup>
                                            <Label for="montantRegle">
                                                {t('reglement.montantRegle')} *
                                            </Label>
                                            <Field
                                                name="montantRegle"
                                                type="number"
                                                onFocus={(e) => {
                                                    e.target.select();
                                                    e.target.addEventListener('wheel', preventScroll, { passive: false });
                                                }}
                                                onBlur={(e) => {
                                                    e.target.removeEventListener('wheel', preventScroll);
                                                }}

                                                className={`form-control ${errors.montantRegle && touched.montantRegle ? 'is-invalid' : ''}`}
                                                placeholder="0.00"
                                                value={values.montantRegle}
                                                onChange={(e) => {
                                                    if (canEdit) {
                                                        setFieldValue('montantRegle', e.target.value);
                                                    }
                                                }}
                                                disabled={!canEdit}
                                            />
                                            <ErrorMessage name="montantRegle" component="div" className="invalid-feedback" />


                                        </FormGroup>
                                    </Col>
                                    <Col md={3}>
                                        <FormGroup>
                                            <Label for="modeReglement">{t('reglement.modeReglement')} *</Label>
                                            <Field
                                                as="select"
                                                name="modeReglement"
                                                className={`form-control ${errors.modeReglement && touched.modeReglement ? 'is-invalid' : ''}`}
                                                disabled={!canEdit}
                                            >
                                                <option value="">{t('reglement.selectModeReglement')}</option>
                                                {modeReglementOptions.map(option => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </Field>
                                            <ErrorMessage name="modeReglement" component="div" className="invalid-feedback" />
                                        </FormGroup>
                                    </Col>
                                    {(values.modeReglement === 'CHEQUE') && (
                                        <>
                                            <Col md={2}>
                                                <FormGroup>
                                                    <Label for="numeroCheque">{t('reglement.numeroCheque')}</Label>
                                                    <Field
                                                        name="numeroCheque"
                                                        type="text"
                                                        className="form-control"
                                                        placeholder={
                                                            values.modeReglement === 'CHEQUE'
                                                        }
                                                        disabled={!canEdit}
                                                    />
                                                </FormGroup>
                                            </Col>
                                            <Col md={2}>
                                                <FormGroup>
                                                    <Label for="banque">{t('reglement.banque')}</Label>
                                                    <Input
                                                        type="select"
                                                        id="banque"
                                                        name="banque"
                                                        value={values.banque}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        invalid={touched.banque && errors.banque}
                                                        disabled={!canEdit}
                                                    >
                                                        <option value="">{t('payment.selectBank', 'Sélectionner une banque')}</option>
                                                        <option value="BMCE">BMCE</option>
                                                        <option value="ATTIJARI">ATTIJARI</option>
                                                        <option value="CIH">CIH</option>
                                                        <option value="CREDIT DU MAROC">CREDIT DU MAROC</option>
                                                        <option value="SG">SG</option>
                                                    </Input>
                                                </FormGroup>
                                            </Col>
                                            <Col md={2}>
                                                <FormGroup>
                                                    <Label for="dateEcheance">
                                                        <FaCalendarAlt className="me-1" />
                                                        {t('reglement.dateEcheance')}
                                                    </Label>
                                                    <Field
                                                        name="dateEcheance"
                                                        type="date"
                                                        className="form-control"
                                                        disabled={!canEdit}
                                                    />
                                                </FormGroup>
                                            </Col>
                                        </>

                                    )}
                                    {(values.modeReglement === 'VIREMENT') && (
                                        <Col md={2}>
                                            <FormGroup>
                                                <Label for="referencePaiement">{t('reglement.referencePaiement')}</Label>
                                                <Field
                                                    name="referencePaiement"
                                                    type="text"
                                                    className="form-control"
                                                    disabled={!canEdit}
                                                />
                                            </FormGroup>
                                        </Col>
                                    )}
                                </Row>



                                <Row className="mt-3">
                                    <Col md={12}>
                                        <FormGroup>
                                            <Label for="commentaire">{t('reglement.commentaire')}</Label>
                                            <Field
                                                as="textarea"
                                                name="commentaire"
                                                rows="3"
                                                className="form-control"
                                                placeholder={t('reglement.commentairePlaceholder')}
                                                disabled={!canEdit}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>


                                {/* Allocations Table (Edit Mode) */}
                                {reglement && Array.isArray(reglement.allocations) && reglement.allocations.length > 0 && (
                                    <Card className="mt-3 border-info">
                                        <CardHeader className="bg-info text-white py-2">
                                            <h6 className="mb-0">{t('reglement.allocationsPayments', 'Affectations du règlement')}</h6>
                                        </CardHeader>
                                        <CardBody>
                                            <div className="table-responsive">
                                                <Table bordered hover size="sm">
                                                    <thead className="table-info">
                                                        <tr>
                                                            <th>{t('reglement.documentType', 'Type')}</th>
                                                            <th>{t('reglement.documentNumber', 'N° Document')}</th>
                                                            <th>{t('reglement.montantDocument', 'Montant Document')}</th>
                                                            <th>{t('reglement.montantAlloue', 'Montant Alloué')}</th>
                                                            <th>{t('reglement.montantRestant', 'Montant Restant')}</th>
                                                            <th>{t('reglement.commentaire', 'Commentaire')}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {reglement.allocations.map(allocation => (
                                                            <tr key={allocation.id}>
                                                                <td>{allocation.typeDocument}</td>
                                                                <td>{allocation.numeroDocument}</td>
                                                                <td>{formatPrice(allocation.montantDocument)}</td>
                                                                <td className="fw-bold text-success">{formatPrice(allocation.montantAlloue)}</td>
                                                                <td className="fw-bold text-danger">{formatPrice(allocation.montantRestant)}</td>
                                                                <td>{allocation.commentaire || '-'}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                    <tfoot>
                                                        <tr>
                                                            <td colSpan={2} className="fw-bold text-end">{t('reglement.total', 'Total')}:</td>
                                                            <td className="fw-bold">{formatPrice(reglement.allocations.reduce((acc, allocation) => acc + allocation.montantDocument, 0))}</td>
                                                            <td className="fw-bold text-success">{formatPrice(reglement.allocations.reduce((acc, allocation) => acc + allocation.montantAlloue, 0))}</td>
                                                            <td className="fw-bold text-danger">{formatPrice(reglement.allocations.reduce((acc, allocation) => acc + allocation.montantRestant, 0))}</td>
                                                            <td></td>
                                                        </tr>
                                                    </tfoot>
                                                </Table>
                                            </div>
                                        </CardBody>
                                    </Card>
                                )}
                            </CardBody>
                        </Card>
                        {/* Boutons d'action */}
                        <Card className="mt-1 shadow-sm">
                            <CardBody>
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        {selectedDocuments.length > 0 && (
                                            <small className="text-muted">
                                                {selectedDocuments.length} {t('reglement.documentsSelectionnes')} -
                                                {t('reglement.total')}: <strong>{formatPrice(calculateTotalAmount())}</strong>
                                            </small>
                                        )}
                                    </div>
                                    {
                                            canEdit &&<div className="d-flex gap-2">
                                        
                                            <Button
                                                type="submit"
                                                color="primary"
                                                disabled={
                                                    isSubmitting ||
                                                    (!selectedClient) ||
                                                    (typeReglement === 'AVANCE' && (!values.montantRegle || values.montantRegle <= 0)) ||
                                                    (!reglement && (typeReglement === 'FACTURE' || typeReglement === 'BON_LIVRAISON') && selectedDocuments.length === 0)
                                                }
                                            >
                                                {isSubmitting && <Spinner size="sm" className="me-2" />}
                                                <i className={`fas ${reglement ? 'fa-save' : 'fa-plus'} me-1`}></i>
                                                {reglement ? t('common.update') : t('common.create')}
                                            </Button>
                                        

                                    </div>}
                                </div>
                            </CardBody>
                        </Card>
                    </Form>
                )}
            </Formik>

            {/* Modal de sélection des documents */}
            <DocumentSelectionModal
                isOpen={showDocumentModal}
                toggle={() => setShowDocumentModal(false)}
                typeDocument={typeReglement}
                documents={
                    typeReglement === "AVANCE"
                        ? (
                            avanceDocFilter === "NONE"
                                ? []
                                : [
                                    ...(avanceDocFilter === "FACTURE" || avanceDocFilter === "BOTH" ? facturesImpayees.filter(f => f.statut != 'ANNULEE' && f.statut != 'PAYEE') : []),
                                    ...(avanceDocFilter === "BL" || avanceDocFilter === "BOTH" ? bonLivraisonsNonRegles : [])
                                ]
                        )
                        : typeReglement === "FACTURE"
                            ? facturesImpayees
                            : bonLivraisonsNonRegles
                }
                loading={loading}
                selectedDocuments={selectedDocuments}
                onDocumentsSelected={handleDocumentsSelected}
            />
            <Modal isOpen={showEcheanceModal} toggle={() => setShowEcheanceModal(false)} size="lg">
                <ModalHeader toggle={() => setShowEcheanceModal(false)} className="bg-warning bg-opacity-10 border-0">
                    <FaCalendarAlt className="me-2 text-warning" />
                    <span className="fw-bold text-warning">{t('client.echeancesDetails')}</span>
                </ModalHeader>
                <ModalBody>
                    {echeances && echeances.length > 0 ? (
                        <div>
                            <div className="mb-3 d-flex justify-content-between align-items-center">
                                <span className="fw-bold text-warning fs-5">
                                    <FaCalendarAlt className="me-2" /> {t('client.echeances')}: {echeances.length}
                                </span>
                                <span className="fw-bold text-muted">{t('echeance.total')}: <span className="text-warning">{formatPrice(getTotalMontant(echeances))}</span></span>
                            </div>
                            <Table bordered hover responsive className="table-sm">
                                <thead className="bg-warning bg-opacity-10">
                                    <tr>
                                        <th>{t('echeance.reference')}</th>
                                        <th>{t('echeance.dateReglement')}</th>
                                        <th>{t('echeance.numeroCheque')}</th>
                                        <th>{t('echeance.banque')}</th>
                                        <th>{t('echeance.montantCheque')}</th>
                                        <th>{t('echeance.dateEcheance')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {echeances.map((echeance, idx) => (
                                        <tr key={idx}>
                                            <td>{echeance.reference}</td>
                                            <td>{echeance.dateReglement}</td>
                                            <td>{echeance.numeroCheque}</td>
                                            <td>{echeance.banque}</td>
                                            <td className="text-end fw-bold text-warning">{formatPrice(echeance.montantRegle)}</td>
                                            <td>{echeance.dateEcheance}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    ) : (
                        <div className="text-muted text-center py-4">{t('client.noEcheances')}</div>
                    )}
                </ModalBody>
            </Modal>
            <Modal isOpen={showCautionModal} toggle={() => setShowCautionModal(false)} size="lg">
                <ModalHeader toggle={() => setShowCautionModal(false)} className="bg-info bg-opacity-10 border-0">
                    <FaShieldAlt className="me-2 text-info" />
                    <span className="fw-bold text-info">{t('client.cautionsDetails')}</span>
                </ModalHeader>
                <ModalBody>
                    {cautions && cautions.length > 0 ? (
                        <div>
                            <div className="mb-3 d-flex justify-content-between align-items-center">
                                <span className="fw-bold text-info fs-5">
                                    <FaShieldAlt className="me-2" /> {t('client.cautions')}: {cautions.length}
                                </span>
                                <span className="fw-bold text-muted">{t('caution.total')}: <span className="text-info">{formatPrice(getTotalMontant(cautions))}</span></span>
                            </div>
                            <Table bordered hover responsive className="table-sm">
                                <thead className="bg-info bg-opacity-10">
                                    <tr>
                                        <th>{t('caution.reference')}</th>
                                        <th>{t('caution.dateReglement')}</th>
                                        <th>{t('caution.numeroCheque')}</th>
                                        <th>{t('caution.banque')}</th>
                                        <th>{t('caution.montantCheque')}</th>
                                        <th>{t('caution.dateEcheance')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {cautions.map((caution, idx) => (
                                        <tr key={idx}>
                                            <td>{caution.reference}</td>
                                            <td>{caution.dateReglement}</td>
                                            <td>{caution.numeroCheque}</td>
                                            <td>{caution.banque}</td>
                                            <td className="text-end fw-bold text-info">{formatPrice(caution.montantRegle)}</td>
                                            <td>{caution.dateEcheance}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    ) : (
                        <div className="text-muted text-center py-4">{t('client.noCautions')}</div>
                    )}
                </ModalBody>
            </Modal>
        </Container>
    );
};

export default ReglementForm;

